import { MileageUpdateBadge } from '../mileage/MileageUpdateBadge';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { CheckCircle, XCircle, Download } from 'lucide-react';
import PermButton from '../shared/PermButton';
import { dateLocale } from '../../lib/dateFormat';
import { DateText } from '../ui/week-badge';

const PendingApprovalsTab = ({
  expenses,
  formatCurrency,
  getProjectName,
  getStatusBadge,
  onApprove,
  onReject,
  onExport
}) => {
  const { t } = useTranslation();
  const pendingExpenses = expenses.filter(e => ['submitted', 'manager_approved'].includes(e.status));

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{t('budget.pendingApprovals')}</CardTitle>
            <CardDescription>
              {t('budget.expensesWaitingForYourApproval')}
            </CardDescription>
          </div>
          {onExport && (
            <Button variant="outline" size="sm" onClick={onExport} data-testid="btn-export">
              <Download className="h-4 w-4 mr-2" /> {t('budget.export')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('budget.date')}</TableHead>
              <TableHead>{t('budget.project')}</TableHead>
              <TableHead>{t('budget.category')}</TableHead>
              <TableHead>{t('budget.description')}</TableHead>
              <TableHead className="text-right">{t('budget.amountPln')}</TableHead>
              <TableHead>{t('budget.status')}</TableHead>
              <TableHead>{t('budget.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingExpenses.map(expense => (
              <TableRow key={expense.id}>
                <TableCell>
                  <DateText value={expense.expense_date} />
                </TableCell>
                <TableCell>{getProjectName(expense.project_id)}</TableCell>
                <TableCell className="capitalize">{expense.category}</TableCell>
                <TableCell>{expense.description || expense.subcategory}</TableCell>
                <TableCell className="text-right">
                  {formatCurrency(expense.amount_pln)}
                </TableCell>
                <TableCell><div className="flex flex-wrap items-center gap-1">{getStatusBadge(expense.status)}<MileageUpdateBadge expense={expense} /></div></TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <PermButton
                      perm="expenses.approve"
                      size="sm"
                      variant="outline"
                      className="text-green-600"
                      onClick={() => onApprove(expense)}
                      data-testid={`approve-expense-${expense.id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" /> {t('budget.approve')}
                    </PermButton>
                    <PermButton
                      perm="expenses.approve"
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      onClick={() => onReject(expense)}
                      data-testid={`reject-expense-${expense.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> {t('budget.reject')}
                    </PermButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {pendingExpenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  {t('budget.noPendingApprovals')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default PendingApprovalsTab;
